import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { GEOSERVER_FEATURE_INFO_URL } from '@core/constants/constant.data'
import { SelectionToolService } from '@core/services/selection-tool.service'

import { MAP } from '@bases/base.token'
import { ScenarioService } from '@components/panel/tabs/scenario-tab/scenario.service'
import {
  COUNTRY,
  COUNTRY_GEOSERVER,
  HECTARE,
  LAU2,
  REGION,
  REGION_GEOSERVER,
  SUBDIVISION,
  SUBDIVISION_GEOSERVER,
} from '@core/constants/scale.data'
import { environment } from '@env/environment'
import { ThemeService } from '@pages/map/theme.service'
import { FeatureCollection } from 'geojson'
import * as L from 'leaflet'
import { BehaviorSubject } from 'rxjs'
import { CalculationModuleService } from './calculation-module.service'
import { CMLayersService } from './cm-layers.service'
import { isNullOrUndefinedString } from './core.utilities'
import { UploadedLayersService } from './layers-uploaded.service'
import { LayersService } from './layers.service'
import { SelectionScaleService } from './selection-scale.service'
import { ToasterService } from './toaster.service'
import LatLng = L.LatLng

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private readonly _map = inject<BehaviorSubject<L.DrawMap>>(MAP)
  private readonly _http = inject(HttpClient)
  private readonly _toasterService = inject(ToasterService)
  private readonly _layersService = inject(LayersService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _cmLayerService = inject(CMLayersService)
  private readonly _scenarioService = inject(ScenarioService)
  private readonly _selectionToolService = inject(SelectionToolService)
  private readonly _calculationModuleService = inject(CalculationModuleService)
  private readonly _themeService = inject(ThemeService)
  private readonly _selectionScaleService = inject(SelectionScaleService)

  isCMRunning$ = this._calculationModuleService.isCMRunning$
  backgroundMaps = new Map<string, L.LayerGroup>()
  currentBackgroundMap: string = ''
  private _clickAccuracy = 100
  private _isProcessingClickOnMap = false

  constructor() {
    const darkMode = localStorage.getItem('darkMode')
    if (darkMode && JSON.parse(darkMode)) {
      this._themeService.setTheme('dark')
    }
  }

  // Retrieve all map events
  private _retrieveMapEvent(): void {
    this._map.getValue().on('click', (event: L.LeafletMouseEvent) => {
      this._onClickEvent(event)
    })
    this._map.getValue().on(L.Draw.Event.CREATED, (event: L.LeafletEvent) => {
      this._onDrawCreated(event as L.DrawEvents.Created)
    })
  }

  // Event functions
  private _onDrawCreated(event: L.DrawEvents.Created) {
    this._selectionToolService.drawCreated(event)
  }

  private async _onClickEvent(e: L.LeafletMouseEvent) {
    if (this._isProcessingClickOnMap) {
      return
    }
    if (this.isCMRunning$()) {
      this._toasterService.openSnackBar('Stop current CM to change selection')
      return
    }

    this._isProcessingClickOnMap = true

    try {
      const scaleLevelDisplayName = this._selectionScaleService.currentScaleLevel$()?.displayName
      let selectedRegion
      // Check if the selection tool is activate
      if (scaleLevelDisplayName === HECTARE) {
        return
      } else if (scaleLevelDisplayName === COUNTRY) {
        selectedRegion = await this._getNutsGeometryFromCountryAfrica(e.latlng)
      } else if (scaleLevelDisplayName === REGION) {
        selectedRegion = await this._getNutsGeometryFromRegionAfrica(e.latlng)
      } else if (scaleLevelDisplayName === SUBDIVISION) {
        selectedRegion = await this._getNutsGeometryFromSubregionAfrica(e.latlng)
      } else if (scaleLevelDisplayName === LAU2) {
        selectedRegion = await this._getNutsGeometryFromLau2(e.latlng)
      } else {
        //other NUTS
        selectedRegion = await this._getNutsGeometryFromNuts(e.latlng)
      }

      // Clean current drawing and reset tools in case of unfinished selection
      if (
        this._selectionToolService.theDrawer &&
        (this._selectionToolService.theDrawer instanceof L.Draw.Circle ||
          this._selectionToolService.theDrawer instanceof L.Draw.Rectangle)
      ) {
        this._selectionToolService.deactivateDrawTool()
        this._selectionToolService.buttonToolSelected$.set('click')
        this.selectAreaWithNuts(selectedRegion)
        return
      }

      if (selectedRegion) {
        this.selectAreaWithNuts(selectedRegion)
      }
    } finally {
      setTimeout(() => {
        this._isProcessingClickOnMap = false
      }, 300)
    }
  }

  /////////////////////
  // Selection
  /////////////////////

  private async _getNutsGeometryFromNuts(latlng: LatLng): Promise<any> {
    const action = this._selectionScaleService.currentScaleLevel$()?.geoServer
    if (action) {
      const area = await this._getNutsGeometry(action, latlng)
      if (area?.features?.length > 0) {
        return area
      } else {
        this._toasterService.openSnackBar(
          'The selected area does not contain data for this scale level.',
        )
        return null
      }
    } else {
      return null
    }
  }
  // LAU management;
  private _getNutsGeometryFromLau2(latLng: LatLng): Promise<any> {
    return this._getNutsGeometry('lau2', latLng, 'hotmaps:')
  }
  private _getNutsGeometryFromCountryAfrica(latLng: LatLng): Promise<any> {
    return this._getNutsGeometry(COUNTRY_GEOSERVER, latLng)
  }
  private _getNutsGeometryFromRegionAfrica(latLng: LatLng): Promise<any> {
    return this._getNutsGeometry(REGION_GEOSERVER, latLng)
  }
  private _getNutsGeometryFromSubregionAfrica(latlng: LatLng): Promise<any> {
    return this._getNutsGeometry(SUBDIVISION_GEOSERVER, latlng)
  }
  private _getNutsGeometry(
    action: string,
    latlng: LatLng,
    prefix: string = environment.prefixWorkspaceName,
  ): Promise<any> {
    const bbox = latlng.toBounds(this._clickAccuracy).toBBoxString()
    const url =
      GEOSERVER_FEATURE_INFO_URL +
      action +
      '&STYLES&LAYERS=' +
      prefix +
      action +
      '&INFO_FORMAT=application/json&FEATURE_COUNT=50' +
      '&X=50&Y=50&SRS=EPSG:4326&WIDTH=101&HEIGHT=101&BBOX=' +
      bbox
    return this._getAreaFromScale(url)
  }
  private _getAreaFromScale(url: string): Promise<any> {
    return new Promise((resolve) => {
      this._http.get(url).subscribe({
        next: (res: any) => {
          resolve(res)
        },
        error: (error) => {
          console.error(error)
          // To override 404 message of geoserver
          this._toasterService.openSnackBarError('Error fetching area from scale ')
          resolve(null)
        },
      })
    })
  }
  selectAreaWithNuts(areaSelected: FeatureCollection) {
    // test if polygon tool is activated in order to avoid selecting a nuts during a polygon drawing

    if (this._selectionToolService.buttonToolSelected$() == 'click') {
      if (isNullOrUndefinedString(areaSelected)) return

      const areaNutsSelectedLayer = L.geoJSON(areaSelected)

      if (this._selectionToolService.containLayer(areaNutsSelectedLayer)) {
        this._selectionToolService.removeLayerFromMultiSelectionLayers(areaNutsSelectedLayer)
        // still fetches geojson even if already on the map, could just make selection delete itself on click
      } else if (!this._selectionToolService.containLayer(areaNutsSelectedLayer)) {
        this._selectionToolService.addToMultiSelectionLayers(areaNutsSelectedLayer)
      }
    }
  }

  /////////////////////
  // End of Selection
  /////////////////////

  setupMapService() {
    this._retrieveMapEvent()
    this._layersService.layersStandard.addTo(this._map.getValue())
    this._cmLayerService.layersCM.addTo(this._map.getValue())
    this._uploadedLayersService.uploadedLayersFeatureGroup.addTo(this._map.getValue())
    this._scenarioService.layersScenario.addTo(this._map.getValue())
    this._selectionToolService.multiSelectionLayers.addTo(this._map.getValue())
  }

  initBackgroundMaps() {
    // OSM Tile Layer
    const osmLayer = L.tileLayer('https://{s}.tile.osm.org/{z}/{x}/{y}.png', {
      zIndex: 1,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>,' +
        ' Tiles courtesy of <a href="https://hot.openstreetmap.org/" target="_blank">Humanitarian OpenStreetMap Team</a>',
    })

    const osmLayerGroup = L.layerGroup([osmLayer])

    // Satellite Tile Layer
    const satelliteLayerUrl =
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    const satelliteLayer = L.tileLayer(satelliteLayerUrl, {
      attribution:
        'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC,' +
        ' USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan,',
    })

    // Reference Layer on top of Satellite
    const referenceLayerUrl =
      'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
    const referenceLayer = L.tileLayer(referenceLayerUrl, {
      zIndex: 1,
      attribution:
        'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC,' +
        ' USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan,',
    })

    // Group layers to have both images and names
    const satelliteGroupLayer = L.layerGroup([satelliteLayer, referenceLayer])

    this.backgroundMaps.set('Road', osmLayerGroup)
    this.backgroundMaps.set('Satellite', satelliteGroupLayer)

    this.currentBackgroundMap = 'Satellite'
    satelliteGroupLayer.addTo(this._map.getValue())
  }

  setBackgroundMap(mapKey: string) {
    this.currentBackgroundMap = mapKey
    const mapInstance = this._map.getValue()
    const layerToAdd = this.backgroundMaps.get(mapKey)
    if (layerToAdd) {
      mapInstance.addLayer(layerToAdd)
    }
    // Remove all other background layers
    for (const [key, layer] of this.backgroundMaps.entries()) {
      if (key !== mapKey && mapInstance.hasLayer(layer)) {
        mapInstance.removeLayer(layer)
      }
    }
  }

  getBackgroundLayerCopy(): L.LayerGroup | null {
    const originalGroup = this.backgroundMaps.get(this.currentBackgroundMap)

    const newGroup = new L.LayerGroup()
    originalGroup?.eachLayer((layer) => {
      newGroup.addLayer(L.tileLayer((layer as any)._url, { ...(layer as any).options }))
    })

    return newGroup
  }
}
